import { useState } from "react";
import apiClient from "@/services/apiClient";
import ToastHelper from "@/utils/toastHelper";
import { CheckoutValidationCode } from "@/constants/checkoutValidationCode";
import { parseCalendarDate } from "@/utils/dateUtils";

export const useStudentBooking = (course, courseScheduleId) => {
  const [step, setStep] = useState("pay");
  const [student, setStudent] = useState({ firstName: "", lastName: "", email: "", mobile: "" });
  const [processing, setProcessing] = useState(false);
  const [checkingExisting, setCheckingExisting] = useState(false);
  const [bookingResponse, setBookingResponse] = useState(null);

  const formatTime = (value) => {
    if (!value) return "";
    const [hours = "0", minutes = "0"] = String(value).split(":");
    const date = new Date();
    date.setHours(Number(hours), Number(minutes), 0, 0);
    return date.toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" });
  };

  const getScheduleDate = (course) => {
    const rawDate = course?.ScheduleDate || course?.scheduleDate || course?.date;
    if (!rawDate) return null;
    const parsed = parseCalendarDate(rawDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const getScheduleTime = (course) => {
    const start = course?.StartTime || course?.startTime;
    const end = course?.EndTime || course?.endTime;
    if (start && end) return `${formatTime(start)} - ${formatTime(end)}`;
    return course?.time || "";
  };

  const getLocationText = (course) => {
    const campusName = course?.CampusName || course?.campusName || course?.location;
    const suburb = course?.Suburb || course?.suburb;
    const postcode = course?.PostCode || course?.postCode;
    return [campusName, suburb, postcode].filter(Boolean).join(", ");
  };

  const getTrainerName = (course) => course?.TrainerName || course?.trainerName || course?.trainer || "TBA";

  const courseFee = Number(course?.CourseFee ?? course?.courseFee ?? course?.price ?? 0);
  const subtotal = courseFee;
  const gst = 0;
  const discount = 0;
  const total = courseFee;
  const scheduleDate = getScheduleDate(course);
  const scheduleTime = getScheduleTime(course);
  const locationText = getLocationText(course);
  const trainerName = getTrainerName(course);

  const getIsoDateOfBirth = (value) => {
    const trimmedValue = String(value || "").trim();
    if (!trimmedValue) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      const [yearRaw, monthRaw, dayRaw] = trimmedValue.split("-");
      const year = Number(yearRaw);
      const month = Number(monthRaw);
      const day = Number(dayRaw);
      const parsed = new Date(Date.UTC(year, month - 1, day));

      if (
        Number.isNaN(parsed.getTime()) ||
        parsed.getUTCFullYear() !== year ||
        parsed.getUTCMonth() !== month - 1 ||
        parsed.getUTCDate() !== day
      ) {
        return null;
      }

      return `${yearRaw}-${monthRaw}-${dayRaw}T00:00:00`;
    }

    const parts = trimmedValue.split("/").map((part) => part.trim());
    if (parts.length !== 3) return null;

    const [dayRaw, monthRaw, yearRaw] = parts;
    if (!/^\d{1,2}$/.test(dayRaw) || !/^\d{1,2}$/.test(monthRaw) || !/^\d{4}$/.test(yearRaw)) {
      return null;
    }

    const day = Number(dayRaw);
    const month = Number(monthRaw);
    const year = Number(yearRaw);
    const parsed = new Date(Date.UTC(year, month - 1, day));

    if (
      Number.isNaN(parsed.getTime()) ||
      parsed.getUTCFullYear() !== year ||
      parsed.getUTCMonth() !== month - 1 ||
      parsed.getUTCDate() !== day
    ) {
      return null;
    }

    return `${yearRaw}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
  };

  const createPaymentIFrame = async (courseId, studentData) => {
    const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
    const externalId = `GIT-${courseId}-${Date.now()}`;
    const response = await apiClient.post(`${apiBaseUrl}/PayAdvantage/iframe`, {
      amount: Number(total.toFixed(2)),
      description: `GIT course booking - schedule ${courseId}`,
      externalId,
      firstName: studentData.firstName,
      lastName: studentData.lastName,
      email: studentData.email,
      mobileNumber: studentData.mobile,
    });
    return response.data;
  };

  const checkExistingBooking = async (courseId, email) => {
    setCheckingExisting(true);

    try {
      const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
      const response = await apiClient.post(`${apiBaseUrl}/StudentBooking/CheckExisting`, {
        Email: email,
        CourseScheduleId: courseId,
      }, {
        headers: { "Content-Type": "application/json" },
      });

      return response.data;
    } catch (error) {
      return error?.response?.data || {
        success: false,
        hasExistingBooking: false,
        validationCode: CheckoutValidationCode.None,
        message: error?.message || "Unable to validate this booking right now.",
      };
    } finally {
      setCheckingExisting(false);
    }
  };

  const submitBooking = async (studentData, courseId, paymentReference, paymentResponse, options = {}) => {
    setProcessing(true);

    try {
      const apiBaseUrl = (process.env.REACT_APP_API_URL).replace(/\/$/, "");
      const payload = {
        FirstName: studentData.firstName,
        LastName: studentData.lastName,
        Email: studentData.email,
        MobileNumber: studentData.mobile,
        DateOfBirth: getIsoDateOfBirth(options.dateOfBirth),
        CourseScheduleId: courseId,
        CourseFee: Number(courseFee.toFixed(2)),
        GST: Number(gst.toFixed(2)),
        Discount: Number(discount.toFixed(2)),
        TotalAmount: Number(total.toFixed(2)),
        PaymentReference: paymentReference,
        SpecialRequest: options.specialRequest || null,
        PaymentResponse: paymentResponse,
      };

      if (!payload.DateOfBirth) {
        throw new Error("Please enter a valid date of birth.");
      }

      const response = await apiClient.post(`${apiBaseUrl}/StudentBooking`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setBookingResponse(response.data);
      setStep("done");

      if (response.data?.success === true && response.data?.statusCode === 1) {
        ToastHelper.success("Booking submitted successfully!");
      } else {
        ToastHelper.error(response.data?.message || "Unable to complete your booking.");
      }

      return response.data;
    } catch (error) {
      console.error("Failed to submit booking", error);
      const apiError = error?.response?.data;
      if (apiError?.validationCode === CheckoutValidationCode.DuplicateBooking) {
        return {
          success: false,
          duplicateBooking: true,
          message: apiError.message || "A booking for this course already exists using this email address. Please sign in to your student account to continue your enrolment. If you need assistance, please contact Student Services.",
        };
      }
      if (apiError?.validationCode === CheckoutValidationCode.ClassNotInFuture) {
        return {
          success: false,
          classUnavailable: true,
          message: apiError.message || "This class is no longer available for online booking. Please select a future class from the course calendar.",
        };
      }
      const validationErrors = apiError?.errors
        ? Object.values(apiError.errors).flat().join(" ")
        : "";
      const message =
        validationErrors ||
        apiError?.message ||
        apiError?.Message ||
        error?.message ||
        "Unable to submit booking right now.";
      const failedResponse = { success: false, statusCode: 0, message };
      setBookingResponse(failedResponse);
      setStep("done");
      ToastHelper.error(message);
      return failedResponse;
    } finally {
      setProcessing(false);
    }
  };

  return {
    // State
    step,
    setStep,
    student,
    setStudent,
    processing,
    checkingExisting,
    discount,
    bookingResponse,

    // Calculated values
    courseFee,
    subtotal,
    gst,
    total,
    scheduleDate,
    scheduleTime,
    locationText,
    trainerName,

    // Methods
    checkExistingBooking,
    createPaymentIFrame,
    submitBooking,
  };
};
